import ComponentManager from '@/Management/ComponentManager';
import ViewFactory from '@/Factories/ViewFactory';
import type ListViewViewModelT from '@/ViewModels/Views/ListViewViewModel';
import type ListViewViewT from '@/Views/ListViewView';
import {
    DisposeBehavior,
    IListViewViewFactoryConstructorParameter,
    IListViewViewModelParameters
} from "#/DefaultViewExtensionInterfaces";

export default class ListViewViewFactory extends ViewFactory {

    /**
     * Die PDO-ID des selektierten Objektes in der ListView
     */
    public selectedPDO: number;

    public openFullScreenSummaryView: boolean;
    public forceSelectingLVByName: boolean
    public notDeletableIds: KnockoutObservable<string[]>;

    /**
     * Ein statischer Filterausdruck
     */
    public filter: string;

    /**
     * Ein Vorfilter
     */
    public preFilter: string;

    /**
     * Ein serialisierter Zustand f�r das FilterViewModel
     */
    public serializedFilter: string;

    /**
     * Definiert die Breite der Factory im Layout
     */
    public colspan: string;

    public isEmbedded: boolean;
    public isVisible: KnockoutObservable<boolean>;
    public contextObject: string;
    public contextTypeName: string;
    public contextRelation: string;
    public ignoreContext: boolean;
    public principalFilterMode: string;
    public disablePrincipalFilter: boolean;
    public showPrincipalFilter: boolean;
    public historyDate: any;
    public titleExtension: any;

    public showButtonCreate: KnockoutObservable<boolean>;
    public showButtonEdit: KnockoutObservable<boolean>;
    public showButtonDelete: KnockoutObservable<boolean>;    

    /**
     * @override
     */
    public viewModel: ListViewViewModelT;

    public constructor(protected params: IListViewViewFactoryConstructorParameter) {
        super(params);
        this.selectedPDO = this.paramOrDefault(params.SelectedPDO, -1);
        this.openFullScreenSummaryView = this.paramOrDefault(params.OpenFullScreenSummaryView, false);
        this.forceSelectingLVByName = this.paramOrDefault(params.forceSelectingLVByName, false);
        this.notDeletableIds = this.observableOrDefault(params.notDeletableIds, []);
        this.filter = this.paramOrDefault(params.Filter, '');
        this.preFilter = this.paramOrDefault(params.PreFilter, '');
        this.serializedFilter = this.paramOrDefault(params.SerializedFilter, null);
        this.colspan = this.paramOrDefault(params.colspan, 'One');

        this.isEmbedded = this.paramOrDefault(params.isEmbedded, false);
        this.isVisible = this.observableOrDefault(params.isVisible, true);
        this.contextObject = this.paramOrDefault(params.ContextObject, "-1");
        this.contextTypeName = this.paramOrDefault(params.ContextTypeName, null);
        this.contextRelation = this.paramOrDefault(params.Assoziation || params.ContextRelation, null);
        this.ignoreContext = this.paramOrDefault(params.IgnoreContext, false) + '' === 'true';
        this.principalFilterMode = this.paramOrDefault(params.PrincipalMode);
        this.disablePrincipalFilter = this.paramOrDefault(params.DisablePrincipalFilter);
        this.showPrincipalFilter = this.paramOrDefault(params.ShowPrincipalButton, true) + '' === 'true';
        this.historyDate = this.paramOrDefault(params.HistoryDate, null);
        this.titleExtension = this.paramOrDefault(params.TitleExtension, null);
        this.viewName = this.paramOrDefault(params.ViewName || params.viewName, null);
        this.viewId = this.paramOrDefault(params.ViewId, null);
        
        this.showButtonCreate = this.observableOrDefault(params.showButtonCreate, true);
        this.showButtonEdit = this.observableOrDefault(params.showButtonEdit, true);
        this.showButtonDelete = this.observableOrDefault(params.showButtonDelete, true);
    }

    public getRegion(): string {
        return 'Content';
    }

    public shouldReplaceRegion(): boolean {
        // Nicht leeren, wenn
        //      1. die Liste eingebettet ist
        //      2. Die View zur Factory noch existiert, d.h. sie wird gerade angezeigt
        return !this.isEmbedded && this.view == null;
    }

    /**
     * @override
     */
    public getView(): JQuery {
        this.resolveDependencies(['Wireframe']);

        if (this.viewModel == null) {
            const params = this.createViewModelParameters();
            this.viewModel = this.createViewModel(params);
        }
        else {
            this.viewModel.raiseHeaderChange();
        }

        if (this.view == null) {
            this.view = this.createView(this.viewModel);
        }

        this.defineComputed(() => {
            const listView = this.viewModel.listView();

            if (listView != null) {
                this.setPageTitle(this.viewModel.pageTitle.toString());
            }
        });

        const tuple = this.view.getTuple();
        return tuple.view;
    }

    protected createViewModel(parameters: IListViewViewModelParameters): ListViewViewModelT {
        const ListViewViewModel = ComponentManager.getViewModel<typeof ListViewViewModelT>('ListViewViewModel');
        return new ListViewViewModel(parameters);
    }

    protected createViewModelParameters(): IListViewViewModelParameters {
        return {
            locator: 'lv',
            viewFactory: this,
            filter: this.filter,
            preFilter: this.preFilter,
            serializedFilter: this.serializedFilter,
            selectedPDO: this.selectedPDO,
            openFullScreenSummaryView: this.openFullScreenSummaryView,
            forceSelectingLVByName: this.forceSelectingLVByName,
            notDeletableIds: this.notDeletableIds,
            showButtonCreate: this.showButtonCreate,
            showButtonEdit: this.showButtonEdit,
            showButtonDelete: this.showButtonDelete,
            contextObject: this.contextObject,
            contextTypeName: this.contextTypeName,
            contextRelation: this.contextRelation,
            ignoreContext: this.ignoreContext,
            principalFilterMode: this.principalFilterMode,
            disablePrincipalFilter: this.disablePrincipalFilter,
            historyDate: this.historyDate,
            showPrincipalFilter: this.showPrincipalFilter,
            viewName: this.viewName,
            viewId: this.viewId,
            isEmbedded: this.isEmbedded,
            titleExtension: this.titleExtension
        }
    }

    protected createView(viewModel: ListViewViewModelT): any {
        const ListViewView = ComponentManager.getView<typeof ListViewViewT>('ListViewView');
        return new ListViewView(viewModel, this.createRootElement('ListView'));
    }

    /**
     * @override
     */
    public refresh(): boolean {
        if (this.viewModel != null && this.viewModel.isDisposed !== true) {
            return this.viewModel.refresh(true);
        }
        return false;
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }

    public dispose() {
        if (this.viewModel != null && this.viewModel.isDisposed !== true) {
            // Die ExitedMessage darf nicht asynchron versendet werden,
            // da ansonsten davon abhängige Logiken nicht mehr funktionieren
            this.viewModel.sendExitedMessage();
        }

        super.dispose();
    }
}

ComponentManager.setFactory('ListView', ListViewViewFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');